import React, { useState, useRef, useEffect } from 'react'
import { Columns2 } from 'lucide-react'
import type { ColumnOption } from '../hooks/useColumnSelector'

interface ColumnSelectorProps {
  columns: ColumnOption[]
  visibleColumns: Set<string>
  onToggle: (id: string) => void
  label?: string
}

const thClass = 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'

export function ColumnSelector({ columns, visibleColumns, onToggle, label = 'Columns' }: ColumnSelectorProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:bg-gray-50"
        title="Select columns to display"
      >
        <Columns2 className="w-4 h-4 text-gray-500" />
        <span className="text-sm">{label}</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-2">
          <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-100">
            Show columns
          </div>
          <div className="max-h-72 overflow-y-auto">
            {columns.map((col) => (
              <label
                key={col.id}
                className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
              >
                <input
                  type="checkbox"
                  checked={visibleColumns.has(col.id)}
                  onChange={() => onToggle(col.id)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-900">{col.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export { thClass }
